import { inject, Injectable, InjectionToken, OnDestroy, signal } from '@angular/core';
import { User, UserManager, UserManagerSettings } from 'oidc-client-ts';
import { AuthService } from '@core/auth/auth.service';
import { IframeService } from '@core/services/iframe.service';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { environment } from 'src/environments/environment';

const DEFAULT_RETURN_URL = '/';
const RETURN_URL_STORAGE_KEY = 'insights-auth-return-url';

// Keep Insights authentication isolated from the primary application client.
const INSIGHTS_USER_MANAGER_SETTINGS: UserManagerSettings = {
  authority: environment.insightsOidc.authority,
  client_id: environment.insightsOidc.clientId,
  redirect_uri: environment.insightsOidc.redirectUri,
  popup_redirect_uri: environment.insightsOidc.redirectUri,
  silent_redirect_uri: environment.insightsOidc.silentRedirectUri,
  scope: environment.insightsOidc.scope,
  response_type: 'code',
  // Stargate blocks framing, so a background renew iframe always fails and would demote a valid session.
  automaticSilentRenew: false,
};

export const INSIGHTS_USER_MANAGER = new InjectionToken<UserManager>('INSIGHTS_USER_MANAGER', {
  providedIn: 'root',
  factory: () => new UserManager(INSIGHTS_USER_MANAGER_SETTINGS),
});

export type InsightsAuthCallbackResult = {
  target: string;
  succeeded: boolean;
};

/**
 * OIDC (PKCE) authentication against the Stargate Insights client.
 * Mirrors CixAuthService but uses a separate client and writes the outcome
 * into AuthService.hasInsightsAccess (token acquired => access granted).
 */
@Injectable({ providedIn: 'root' })
export class InsightsAuthService implements OnDestroy {
  private readonly snackBar = inject(SnackBarService);
  private readonly authService = inject(AuthService);
  private readonly iframeService = inject(IframeService);
  private readonly userManager = inject(INSIGHTS_USER_MANAGER);
  /** True while an interactive sign-in is running, so callers can block duplicate popups. */
  readonly authPending = signal(false);
  private pendingLogin: Promise<boolean> | null = null;
  private pendingRouteAccess: Promise<void> | null = null;
  // Prevent repeated guards from starting duplicate redirects.
  private attemptedNavigationId: number | null = null;
  private routeAccessAttemptInProgress = false;
  // Suppresses one retry after a failed callback to prevent an authentication loop.
  private suppressedReturnUrl: string | null = null;

  constructor() {
    this.userManager.events.addUserLoaded(user => this.authService.hasInsightsAccess.set(this.hasValidAccess(user)));
    this.userManager.events.addUserUnloaded(() => this.authService.hasInsightsAccess.set(false));
    this.userManager.events.addSilentRenewError(() => this.authService.hasInsightsAccess.set(false));
    this.userManager.events.addAccessTokenExpired(() => this.authService.hasInsightsAccess.set(false));
  }

  /**
   * Non-blocking bootstrap auth. Attempts to obtain a token from an existing
   * Stargate session without any user interaction. Does not redirect on failure.
   */
  async initSilent(): Promise<void> {
    if (!this.authService.isInsightsAuthEligible()) {
      this.authService.hasInsightsAccess.set(false);
      return;
    }

    try {
      const user = await this.resolveActiveUser();
      this.authService.hasInsightsAccess.set(this.hasValidAccess(user));
    } catch {
      this.authService.hasInsightsAccess.set(false);
    }
  }

  async ensureAccess(): Promise<boolean> {
    if (this.authService.hasInsightsAccess()) {
      return true;
    }
    if (!this.authService.isInsightsAuthEligible()) {
      this.authService.hasInsightsAccess.set(false);
      return false;
    }
    if (this.pendingLogin) {
      await this.pendingLogin;
      return this.authService.hasInsightsAccess();
    }
    if (this.pendingRouteAccess) {
      await this.pendingRouteAccess;
      return this.authService.hasInsightsAccess();
    }

    await this.initSilent();
    return this.authService.hasInsightsAccess();
  }

  async ensureRouteAccess(returnUrl: string, navigationId: number): Promise<void> {
    const target = this.safeReturnUrl(returnUrl) ?? DEFAULT_RETURN_URL;
    if (!this.authService.isInsightsAuthEligible()) {
      this.authService.hasInsightsAccess.set(false);
      return;
    }
    if (this.suppressedReturnUrl === target) {
      this.suppressedReturnUrl = null;
      return;
    }
    // Guard calls can overlap while Angular is still initializing a route.
    if (this.authService.hasInsightsAccess() || this.attemptedNavigationId === navigationId || this.routeAccessAttemptInProgress) {
      return;
    }

    this.attemptedNavigationId = navigationId;
    this.routeAccessAttemptInProgress = true;
    const routeAccess = this.startRouteAccess(target);
    this.pendingRouteAccess = routeAccess;
    try {
      await routeAccess;
    } finally {
      if (this.pendingRouteAccess === routeAccess) {
        this.pendingRouteAccess = null;
      }
      this.routeAccessAttemptInProgress = false;
    }
  }

  private async startRouteAccess(target: string): Promise<void> {
    // Reuse or silently renew an existing session before showing a sign-in screen.
    await this.initSilent();
    if (!this.authService.hasInsightsAccess()) {
      await this.login(target);
    }
  }

  async login(returnUrl = globalThis.location.pathname + globalThis.location.search): Promise<boolean> {
    if (this.pendingLogin) {
      return this.pendingLogin;
    }

    const target = this.safeReturnUrl(returnUrl) ?? DEFAULT_RETURN_URL;
    this.pendingLogin = this.startLogin(target);
    try {
      return await this.pendingLogin;
    } finally {
      this.pendingLogin = null;
    }
  }

  private async startLogin(target: string): Promise<boolean> {
    this.authPending.set(true);
    try {
      if (this.iframeService.isEmbedded) {
        try {
          const user = await this.userManager.signinPopup({
            state: { returnUrl: target },
            popupAbortOnClose: true,
          });
          const succeeded = this.hasValidAccess(user);
          this.authService.hasInsightsAccess.set(succeeded);
          return succeeded;
        } catch {
          this.authService.hasInsightsAccess.set(false);
          this.snackBar.showError('Insights authentication failed. Please try again.');
          return false;
        }
      }

      // Persist the target because the browser leaves the app during OIDC sign-in.
      this.storeReturnUrl(target);
      try {
        await this.userManager.signinRedirect({
          state: { returnUrl: target },
        });
        return true;
      } catch {
        this.clearStoredReturnUrl();
        this.snackBar.showError('Insights authentication failed. Please try again.');
        return false;
      }
    } finally {
      this.authPending.set(false);
    }
  }

  async completeSigninPopup(): Promise<void> {
    try {
      await this.userManager.signinPopupCallback();
    } catch {
      this.authService.hasInsightsAccess.set(false);
      this.snackBar.showError('Insights authentication callback failed. Please try again.');
    }
  }

  async completeSigninRedirect(): Promise<InsightsAuthCallbackResult> {
    const storedTarget = this.readStoredReturnUrl();
    try {
      const user = await this.userManager.signinCallback();
      const state = (user?.state ?? null) as { returnUrl?: string } | null;
      // Prefer OIDC state, then session storage, and finally the app root.
      const target = this.safeReturnUrl(state?.returnUrl) ?? storedTarget ?? DEFAULT_RETURN_URL;
      const succeeded = this.hasValidAccess(user);
      this.authService.hasInsightsAccess.set(succeeded);
      this.clearStoredReturnUrl();
      if (!succeeded) {
        this.suppressedReturnUrl = target;
        this.snackBar.showError('Insights authentication callback failed. Please try again.');
      }
      return { target, succeeded };
    } catch {
      this.authService.hasInsightsAccess.set(false);
      this.snackBar.showError('Insights authentication callback failed. Please try again.');
      // The callback may fail before OIDC state can be read, so use storage.
      const target = storedTarget ?? DEFAULT_RETURN_URL;
      this.clearStoredReturnUrl();
      this.suppressedReturnUrl = target;
      return { target, succeeded: false };
    }
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.authService.isInsightsAuthEligible()) {
      this.authService.hasInsightsAccess.set(false);
      return null;
    }

    try {
      const user = await this.resolveActiveUser();
      const token = this.hasValidAccess(user) ? (user?.access_token ?? null) : null;
      this.authService.hasInsightsAccess.set(token != null);
      return token;
    } catch {
      this.authService.hasInsightsAccess.set(false);
      return null;
    }
  }

  /** Resolves the current Insights user, silently renewing an expired session but never framing a fresh sign-in. */
  private async resolveActiveUser(): Promise<User | null> {
    const user = await this.userManager.getUser();
    return user?.expired ? this.userManager.signinSilent() : user;
  }

  private hasValidAccess(user: { expired?: boolean; access_token?: string } | null | undefined): boolean {
    return user != null && !user.expired && !!user.access_token;
  }

  private safeReturnUrl(value: unknown): string | null {
    // Only allow same-origin paths to prevent an untrusted redirect target.
    if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
      return null;
    }

    try {
      const url = new URL(value, globalThis.location.origin);
      if (url.origin !== globalThis.location.origin) {
        return null;
      }
      return `${url.pathname}${url.search}`;
    } catch {
      return null;
    }
  }

  private storeReturnUrl(target: string): void {
    try {
      sessionStorage.setItem(RETURN_URL_STORAGE_KEY, target);
    } catch {
      return;
    }
  }

  private readStoredReturnUrl(): string | null {
    try {
      return this.safeReturnUrl(sessionStorage.getItem(RETURN_URL_STORAGE_KEY));
    } catch {
      return null;
    }
  }

  private clearStoredReturnUrl(): void {
    try {
      sessionStorage.removeItem(RETURN_URL_STORAGE_KEY);
    } catch {
      return;
    }
  }

  ngOnDestroy(): void {
    this.userManager.stopSilentRenew();
  }
}
